import * as React from 'react';

/** Material UI */
import {
    Alert,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    List,
    ListItem,
    ListItemText,
    Typography,
} from '@mui/material';

/** Internal Components */
import ThemeButton from 'shared/ui/Button';
import { bulkErpFailureLines, bulkErpResultHeadline } from '../../lib/configUtils';

/**
 * Result of a bulk ERP update — how many entries went through, and which ones did not.
 *
 * A batch can hold a hundred entries, and each of them updating with its own snackbar would leave
 * the operator watching the screen to the end. One window states the outcome instead, and it stays
 * up until it is closed: the entries left behind are named by the reference the operator reads on
 * the grid (Opus Ref / Entry No.), so a timer must not take that list away before it is read.
 *
 * @param {boolean} open - Whether the result window is showing
 * @param {object|null} result - `{ success_count, failure_count, failed }` from the bulk request
 * @param {Function} onClose - Called when the operator closes the window
 */
export default function BulkErpResultDialog({ open, result, onClose }) {
    const successCount = result?.success_count ?? 0;
    const failureCount = result?.failure_count ?? 0;
    const failureLines = bulkErpFailureLines(result?.failed);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontSize: 16, fontWeight: 600 }}>Update ERP result</DialogTitle>
            <DialogContent dividers>
                <Alert severity={failureCount === 0 ? 'success' : 'warning'} sx={{ mb: failureCount ? 2 : 0 }}>
                    {bulkErpResultHeadline(successCount, failureCount)}
                </Alert>
                {failureCount > 0 && (
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
                            These entries were not updated and are still selected:
                        </Typography>
                        <List dense sx={{ maxHeight: 320, overflowY: 'auto', py: 0 }}>
                            {failureLines.map((line) => (
                                <ListItem key={line} sx={{ py: 0.25, px: 0 }}>
                                    <ListItemText
                                        primary={line}
                                        primaryTypographyProps={{ fontSize: 13, sx: { wordBreak: 'break-word' } }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <ThemeButton text="CLOSE" sx={{ height: '30px', px: 2 }} onClick={onClose} />
            </DialogActions>
        </Dialog>
    );
}
